/**
 * The gate panel's shell (gate-panel plan §D) — the second, mobile-first layout next to
 * {@link AppLayout}: no sider, a single compact header (back / title / user pill) and the routed
 * `<Outlet>` under the same per-route error boundary (R-18.60) + `<Suspense>` fallback (R-18.140)
 * discipline. No hardcoded colours (R-18.18) — surfaces come from the theme tokens.
 *
 * The back control always goes UP rather than through history — on a phone the predictable "up"
 * beats a browser-history surprise: from the act form up to its record card (feedback 21.08 — it
 * used to skip to the list), from everywhere else up to the panel home. The main-panel switcher sits in the account
 * menu (feedback 21.08), shows only when the user can actually use the main shell
 * (`canUseMainPanel`), and remembers the choice on this device (gate-panel plan, decision 5).
 */

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Layout, Spin, theme, Typography } from 'antd';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { canUseMainPanel } from './AppLayout';
import { UserPill } from './UserPill';

import { useAuth } from '@/shared/auth/AuthProvider';
import { usePanelStore } from '@/shared/stores/panel-store';
import { RouteErrorBoundary } from '@/shared/ui/AppErrorBoundary';

const { Header, Content } = Layout;

export function GateLayout(): React.JSX.Element {
  const { t } = useTranslation('gate');
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { hasCapability } = useAuth();
  const setPanel = usePanelStore((s) => s.setPanel);

  const atHome = location.pathname === '/gate';
  // "Up", not "back": the act form's parent is its record card; every other screen's parent is the
  // panel home. History could hold anything (a search, another record, the login redirect).
  const upTarget =
    location.pathname.match(/^(\/gate\/records\/[^/]+\/[^/]+)\/act\//)?.[1] ?? '/gate';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingInline: 12,
          background: token.colorBgElevated,
        }}
      >
        {!atHome && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t('layout.back')}
            onClick={() => navigate(upTarget)}
          />
        )}
        <Typography.Text
          strong
          style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {t('layout.title')}
        </Typography.Text>
        {/* The main-panel switcher lives in the account menu (feedback 21.08) — the compact
            mobile header stays uncrowded. */}
        <UserPill
          panelSwitch={
            canUseMainPanel(hasCapability)
              ? {
                  label: t('layout.mainPanel'),
                  onClick: () => {
                    setPanel('main');
                    navigate('/');
                  },
                }
              : null
          }
        />
      </Header>
      {/* AntD `<Content>` renders as `<main>` (R-18.29) — the single page landmark. */}
      <Content style={{ padding: 12 }}>
        <RouteErrorBoundary>
          <Suspense fallback={<Spin description={t('layout.loading')} />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </Content>
    </Layout>
  );
}
